import { Button } from '@avanta/shared/components/ui/Button';
import { Card } from '@avanta/shared/components/ui/Card';
import { Spinner } from '@avanta/shared/components/ui/Spinner';
import { LOCIZE_NS } from '@avanta/shared/constants/common';
import { cn } from '@avanta/shared/utils/cn';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { ROUTES } from 'constants/routes';
import { BAROMETER_AREAS } from 'constants/surveys';
import { GeneralBadge, Battery, WellBeingArea, SurveyBarometer } from './components';
import { BAROMETER_RESULT_AREAS } from './types';
import { useFetchBarometerResults } from './useFetchBarometerResults';

const REPEAT_HINT_CLASS = 'min-w-0 max-w-full text-pretty text-gray-400 leading-5';

export const BarometerResults = (): React.ReactElement | null => {
  const { data: barometerData, isLoading } = useFetchBarometerResults();
  const navigate = useNavigate();
  const { t } = useTranslation(LOCIZE_NS.HOME);

  if (isLoading) {
    return (
      <Card>
        <div className="flex h-72 items-center justify-center">
          <Spinner />
        </div>
      </Card>
    );
  }

  if (barometerData[BAROMETER_AREAS.GENERAL].score === 0) {
    return <SurveyBarometer />;
  }

  const generalResult = barometerData[BAROMETER_AREAS.GENERAL];

  const handleStart = (): void => {
    navigate(ROUTES.WELL_BEING_BAROMETER);
  };

  return (
    <Card className="p-0 px-4 py-6 am-xl:p-6 am-xl:pl-0">
      <div className="flex min-w-0 flex-col gap-6 am-xl:h-full am-xl:flex-row am-xl:justify-between">
        <div className="flex w-full min-w-0 items-start gap-6 am-xl:gap-9 am-xl:pl-14 am-xl:pt-8">
          <Battery
            score={generalResult.score}
            maxScore={generalResult.maxScore}
            className="shrink-0 am-xl:mt-1"
          />
          <div className="flex min-w-0 flex-1 flex-col pt-2 am-xl:self-stretch am-xl:pt-0">
            <div className="flex flex-col gap-2">
              <h2 className="text-denim-800 leading-5 font-medium am-xl:text-xl">
                {t('Pae.BarometerResults.heading')}
              </h2>
              <p className="text-base leading-5 text-denim-500">
                {t('Pae.BarometerResults.description')}
              </p>
            </div>
            <div className="mt-6 flex flex-col gap-1 am-xl:gap-2">
              <p className="text-gray-400 text-xs leading-5 am-xl:text-sm">
                {t('Pae.BarometerResults.averageLabel')}
              </p>
              <GeneralBadge
                score={generalResult.score}
                maxScore={generalResult.maxScore}
                className="w-24 justify-center gap-2 rounded-sm px-4 py-1"
              />
            </div>
            <div className="mt-auto hidden flex-col gap-2 am-xl:flex">
              <Button variant="terciary" size="lg" className="w-fit px-8" onClick={handleStart}>
                {t('Pae.BarometerResults.updateCta')}
              </Button>
              <p className={cn(REPEAT_HINT_CLASS, 'text-sm')}>
                {t('Pae.BarometerResults.repeatHint')}
              </p>
            </div>
          </div>
        </div>
        <div className="flex w-full min-w-0 flex-col gap-2 am-xl:hidden">
          <Button variant="terciary" size="lg" className="w-full px-8" onClick={handleStart}>
            {t('Pae.BarometerResults.updateCta')}
          </Button>
          <p className={cn(REPEAT_HINT_CLASS, 'text-center text-xs')}>
            {t('Pae.BarometerResults.repeatHint')}
          </p>
        </div>
        <div className="flex w-full flex-col gap-2 am-xl:w-66 am-xl:shrink-0 am-xl:gap-4">
          {BAROMETER_RESULT_AREAS.map(area => (
            <WellBeingArea
              key={area}
              area={area}
              showScore
              score={barometerData[area].score}
              maxScore={barometerData[area].maxScore}
            />
          ))}
        </div>
      </div>
    </Card>
  );
};
